import type { components } from "../../api/schema";
import { cn } from "../../lib/cn";
import {
  dayHeaderLabel,
  weekdayLabel,
  type DayGroup,
} from "../../lib/journal";
import { formatSignedMoney } from "../../lib/money";

import { NoteEntry } from "./NoteEntry";
import type { DayPnl } from "./MiniCalendar";

type NoteResponse = components["schemas"]["NoteResponse"];

interface JournalTimelineProps {
  groups: DayGroup<NoteResponse>[];
  dayPnl: Map<string, DayPnl>;
  currency: string;
  selectedDay: string | null;
  tagName: (tagId: string) => string;
  tradeLabel: (note: NoteResponse) => string | null;
  isMutating: boolean;
  onOpenNote: (note: NoteResponse) => void;
  onCreateForDay: (isoDate: string) => void;
  onToggleFavorite: (note: NoteResponse) => void;
  onRestore: (note: NoteResponse) => void;
}

// The mockup's ".tl-spine" day-grouped timeline: each day is an accent node on a
// vertical spine, a serif day header with an optional realized-P&L pill, and the
// day's note cards. Per ADR 0036 D7 the spine lists every trading day, so a day
// with trades but no note shows an "Add note" prompt instead of cards.
export function JournalTimeline({
  groups,
  dayPnl,
  currency,
  selectedDay,
  tagName,
  tradeLabel,
  isMutating,
  onOpenNote,
  onCreateForDay,
  onToggleFavorite,
  onRestore,
}: JournalTimelineProps) {
  return (
    <div data-testid="notes-list" className="flex flex-col">
      {groups.map((group, index) => {
        const pnl = dayPnl.get(group.date);
        const isLast = index === groups.length - 1;
        const isEmpty = group.items.length === 0;
        const noteLabel = group.items.length === 1 ? "1 note" : `${group.items.length} notes`;
        return (
          <div
            key={group.date}
            data-testid={`journal-day-${group.date}`}
            className="grid grid-cols-[26px_1fr] gap-3.5"
          >
            <div className="relative flex justify-center">
              <span className="absolute left-1/2 top-1.5 z-[1] h-[15px] w-[15px] -translate-x-1/2 rounded-full border-[3px] border-primary bg-surface shadow-[0_0_0_4px_var(--color-surface)]" />
              {!isLast ? (
                <span className="absolute left-1/2 top-1.5 -bottom-3 w-0.5 -translate-x-1/2 bg-outline-variant" />
              ) : null}
            </div>

            <div className="flex min-w-0 flex-col gap-2.5 pb-6">
              <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 px-0.5 pb-1.5 pt-1">
                <h2
                  className={cn(
                    "font-display text-2xl tracking-tight text-on-surface",
                    selectedDay === group.date && "underline decoration-primary decoration-2 underline-offset-4",
                  )}
                >
                  {dayHeaderLabel(group.date)}
                </h2>
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-outline">
                  {weekdayLabel(group.date)}
                </span>
                {pnl !== undefined ? (
                  <span
                    className={cn(
                      "rounded-[7px] px-2.5 py-0.5 font-mono text-[13px] font-semibold",
                      Number(pnl.net_pnl_amount) >= 0
                        ? "bg-success-soft text-success"
                        : "bg-danger-soft text-danger",
                    )}
                  >
                    {formatSignedMoney(pnl.net_pnl_amount, { currency })}
                  </span>
                ) : null}
                {!isEmpty ? (
                  <span className="font-mono text-[11px] text-on-surface-muted">{noteLabel}</span>
                ) : null}
              </div>

              {isEmpty ? (
                <button
                  type="button"
                  data-testid={`journal-add-note-${group.date}`}
                  onClick={() => onCreateForDay(group.date)}
                  className="flex items-center gap-2 self-start rounded-[11px] border border-dashed border-outline-variant bg-surface-container px-3.5 py-2.5 text-[12px] font-medium text-on-surface-muted transition-colors hover:border-primary hover:text-on-surface-variant"
                >
                  <span aria-hidden="true" className="text-base leading-none text-primary">+</span>
                  No notes yet — add one for this day
                </button>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {group.items.map((note) => (
                    <NoteEntry
                      key={note.note_id}
                      note={note}
                      tagName={tagName}
                      tradeLabel={tradeLabel(note)}
                      selected={selectedDay === group.date}
                      isMutating={isMutating}
                      onOpen={onOpenNote}
                      onToggleFavorite={onToggleFavorite}
                      onRestore={onRestore}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
